import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import MainLayout from '../../components/layout/MainLayout';
import Icon from '../../components/AppIcon';
import BidStatusChips from './components/BidStatusChips';
import BidCard from './components/BidCard';
import BidDetailView from './components/BidDetailView';
import MobileBottomNav from '../../components/ui/MobileBottomNav';
import { bidService } from '../../services/bidService';
import mobileConfigService from '../../services/mobileConfigService';

const detectPlatform = () => {
  const ua = navigator?.userAgent?.toLowerCase() || '';
  if (/iphone|ipad|ipod/?.test(ua)) return 'ios';
  if (/android/?.test(ua)) return 'android';
  return 'ios';
};

const MobileBidManagement = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [bids, setBids] = useState([]);
  const [activeStatus, setActiveStatus] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedBid, setSelectedBid] = useState(null);
  const [visibility, setVisibility] = useState({ bid_list: true, bid_details: true, financial_info: true });
  const platform = detectPlatform();

  const fetchVisibility = useCallback(async () => {
    try {
      const config = await mobileConfigService?.getScreenVisibility(platform);
      if (config && typeof config === 'object') {
        setVisibility(prev => ({
          ...prev,
          bid_list: config?.bid_list !== false,
          bid_details: config?.bid_details !== false,
          financial_info: config?.financial_info !== false,
        }));
      }
    } catch (err) {
      console.error('Failed to load mobile config:', err);
    }
  }, [platform]);

  const fetchBids = useCallback(async () => {
    try {
      const { data, error } = await bidService?.getAll();
      if (!error && data) setBids(data);
    } catch (err) {
      console.error('Failed to fetch bids:', err);
    }
  }, []);

  const loadAll = useCallback(async (isRefresh = false) => {
    if (isRefresh) setRefreshing(true);
    else setLoading(true);
    await Promise.all([fetchVisibility(), fetchBids()]);
    setLoading(false);
    setRefreshing(false);
  }, [fetchVisibility, fetchBids]);

  useEffect(() => { loadAll(); }, [loadAll]);

  const filteredBids = bids?.filter(bid => {
    const matchesStatus = activeStatus === 'all' || bid?.status === activeStatus;
    if (!searchQuery) return matchesStatus;
    const q = searchQuery?.toLowerCase();
    const matchesSearch =
      (bid?.bidNumber || bid?.bid_number || '')?.toLowerCase()?.includes(q) ||
      (bid?.customerName || bid?.customer_name || '')?.toLowerCase()?.includes(q) ||
      (bid?.projectName || bid?.project_name || '')?.toLowerCase()?.includes(q);
    return matchesStatus && matchesSearch;
  });

  const handleConvertToProject = (bid) => {
    navigate('/project-management', { state: { convertFromBid: bid } });
  };

  const statusCounts = {
    all: bids?.length,
    Draft: bids?.filter(b => b?.status === 'Draft')?.length,
    Submitted: bids?.filter(b => b?.status === 'Submitted')?.length,
    Accepted: bids?.filter(b => b?.status === 'Accepted')?.length,
    Rejected: bids?.filter(b => b?.status === 'Rejected')?.length,
  };

  return (
    <MainLayout>
      <div className="min-h-screen bg-background">
        {/* Header */}
        <div className="sticky top-0 z-40 bg-background/95 backdrop-blur border-b border-border px-4 py-3">
          <div className="flex items-center justify-between mb-3">
            <div>
              <h1 className="text-lg font-bold text-foreground">Bids</h1>
              <p className="text-xs text-muted-foreground capitalize">
                {platform} · {filteredBids?.length} of {bids?.length}
              </p>
            </div>
            <button
              onClick={() => loadAll(true)}
              disabled={refreshing}
              className="w-9 h-9 rounded-full bg-muted flex items-center justify-center"
            >
              <Icon name="RefreshCw" size={16} className={`text-muted-foreground ${refreshing ? 'animate-spin' : ''}`} />
            </button>
          </div>

          {/* Search */}
          {visibility?.bid_list && (
            <div className="relative mb-3">
              <div className="flex items-center gap-2 bg-muted/60 border border-border rounded-2xl px-4 py-3">
                <Icon name="Search" size={18} className="text-muted-foreground flex-shrink-0" />
                <input
                  type="text"
                  value={searchQuery}
                  onChange={e => setSearchQuery(e?.target?.value)}
                  placeholder="Search bids, customers..."
                  className="flex-1 bg-transparent text-sm text-foreground placeholder:text-muted-foreground outline-none"
                />
                {searchQuery && (
                  <button onClick={() => setSearchQuery('')} className="w-5 h-5 rounded-full bg-muted-foreground/30 flex items-center justify-center">
                    <Icon name="X" size={12} className="text-foreground" />
                  </button>
                )}
              </div>
            </div>
          )}

          {/* Status filter chips */}
          {visibility?.bid_list && (
            <BidStatusChips activeStatus={activeStatus} onChange={setActiveStatus} />
          )}
        </div>

        {/* Content */}
        <div className="px-4 py-4 pb-28 space-y-3">
          {loading ? (
            Array.from({ length: 4 })?.map((_, i) => (
              <div key={i} className="bg-card border border-border rounded-2xl p-4 h-28 animate-pulse">
                <div className="flex justify-between mb-2">
                  <div className="space-y-1.5">
                    <div className="h-3 bg-muted rounded w-20" />
                    <div className="h-4 bg-muted rounded w-36" />
                  </div>
                  <div className="h-6 bg-muted rounded w-16" />
                </div>
                <div className="h-3 bg-muted rounded w-28" />
              </div>
            ))
          ) : !visibility?.bid_list ? (
            <div className="flex flex-col items-center justify-center py-16 text-center">
              <div className="w-16 h-16 rounded-2xl bg-muted flex items-center justify-center mb-4">
                <Icon name="EyeOff" size={28} className="text-muted-foreground" />
              </div>
              <h3 className="text-base font-semibold text-foreground mb-1">Section hidden</h3>
              <p className="text-sm text-muted-foreground max-w-xs">
                Bid list is hidden for {platform === 'ios' ? 'iOS' : 'Android'}.
              </p>
              <button onClick={() => navigate('/mobile-app-configuration')} className="mt-4 text-sm font-medium text-accent underline">
                Go to Configuration
              </button>
            </div>
          ) : filteredBids?.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-16 text-center">
              <div className="w-16 h-16 rounded-2xl bg-muted flex items-center justify-center mb-4">
                <Icon name="FileText" size={28} className="text-muted-foreground" />
              </div>
              <h3 className="text-base font-semibold text-foreground mb-1">
                {searchQuery || activeStatus !== 'all' ? 'No matching bids' : 'No bids yet'}
              </h3>
              <p className="text-sm text-muted-foreground">
                {searchQuery ? `No bids match "${searchQuery}"` : activeStatus !== 'all' ? `No ${activeStatus} bids found` : 'Bids will appear here once created.'}
              </p>
            </div>
          ) : (
            filteredBids?.map(bid => (
              <BidCard
                key={bid?.id}
                bid={bid}
                onOpenDetail={setSelectedBid}
              />
            ))
          )}
        </div>

        {/* FAB - New Bid */}
        <button
          onClick={() => navigate('/bid-management')}
          className="fixed bottom-20 right-4 z-40 w-14 h-14 rounded-full bg-accent shadow-xl flex items-center justify-center active:scale-95 transition-transform"
        >
          <Icon name="Plus" size={24} color="white" />
        </button>

        {/* Bid Detail View */}
        {selectedBid && visibility?.bid_details && (
          <BidDetailView
            bid={selectedBid}
            onClose={() => setSelectedBid(null)}
            onConvertToProject={handleConvertToProject}
          />
        )}

        {/* Bottom Nav */}
        <MobileBottomNav />
      </div>
    </MainLayout>
  );
};

export default MobileBidManagement;
